import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import { TOYAMA_LOGO_BASE64 } from './toyama-logo';
import type { MappedChannel } from '$lib/utils/channel_lookup';

const LOGO_BASE64 = TOYAMA_LOGO_BASE64;
const COLORS = {
	ink: '#10223A',
	teal: '#226A73',
	lime: '#97D700',
	paper: '#F8F7F7',
	line: '#DBDBDB',
	muted: '#64748b',
	dangerBg: '#fff4f2',
	infoBg: '#eef6f7',
	successBg: '#f6fbef'
};

type PdfMakeLike = {
	vfs?: Record<string, string>;
	createPdf: (docDefinition: TDocumentDefinitions) => { download: (filename?: string) => void };
};

type LayoutData = {
	project: { name: string };
	unit: { name: string };
	loads: Array<{ id: number; name?: string; load_type?: string; room?: number }>;
	keypads: Array<{ id: number; address?: number; name?: string }>;
	keypadKeys: Array<{ id: number; keypad: number; key_number?: number; key_index?: number; name?: string | null }>;
	keyActions: Array<{ key: number; action_type?: string; action_target?: number; event_type?: string }>;
	sceneLoads: Array<{ scene: number; load?: number; load_id?: number }>;
	rcus: Array<{ id: number; name?: string }>;
	dinModules: Array<{ id: number; module_type?: string; address?: number }>;
	allMappedChannels?: MappedChannel[];
	channels?: Array<{ object_id?: number; load?: number; channel_number?: number }>;
	electricalSummary?: Record<string, unknown> | unknown;
	zones?: unknown[];
	rooms?: unknown[];
	scenes?: unknown[];
};

function layoutChannels(data: LayoutData): MappedChannel[] {
	if (data.allMappedChannels?.length) return data.allMappedChannels;
	return (data.channels ?? [])
		.filter((item) => item.load != null)
		.map((item) => ({
			id: item.object_id ?? 0,
			channel_number: item.channel_number ?? 0,
			load_id: item.load as number,
			source: 'rcu' as const,
			rcu_id: item.object_id
		}));
}

function sceneLoadTarget(sl: { load?: number; load_id?: number }): number | undefined {
	return sl.load ?? sl.load_id;
}

async function loadPdfMake(): Promise<PdfMakeLike> {
	if (typeof window !== 'undefined' && (window as { pdfMake?: PdfMakeLike }).pdfMake) {
		return (window as { pdfMake: PdfMakeLike }).pdfMake;
	}

	try {
		const pdfMakeModule = await import('pdfmake/build/pdfmake');
		const fontsModule = await import('pdfmake/build/vfs_fonts');
		const pdfMake = (pdfMakeModule.default || pdfMakeModule) as PdfMakeLike;
		const fonts = (fontsModule.default || fontsModule) as { pdfMake?: { vfs?: Record<string, string> }; vfs?: Record<string, string> };
		pdfMake.vfs = fonts.pdfMake?.vfs || fonts.vfs || pdfMake.vfs;
		if (typeof window !== 'undefined') (window as { pdfMake?: PdfMakeLike }).pdfMake = pdfMake;
		return pdfMake;
	} catch {
		const loadScript = (src: string) =>
			new Promise<void>((resolve, reject) => {
				const script = document.createElement('script');
				script.src = src;
				script.onload = () => resolve();
				script.onerror = () => reject(new Error(`Failed to load ${src}`));
				document.head.appendChild(script);
			});

		await loadScript('https://unpkg.com/pdfmake/build/pdfmake.min.js');
		await loadScript('https://unpkg.com/pdfmake/build/vfs_fonts.js');
		const fallback = (window as { pdfMake?: PdfMakeLike }).pdfMake;
		if (!fallback) throw new Error('PDF library failed to load');
		return fallback;
	}
}

async function renderMermaidSvg(definition: string): Promise<string> {
	if (!hasMermaidNodes(definition)) return '';
	try {
		const mermaidModule = await import('mermaid');
		const mermaid = mermaidModule.default;
		mermaid.initialize({
			startOnLoad: false,
			theme: 'base',
			themeVariables: {
				primaryColor: '#F8F7F7',
				primaryTextColor: '#10223A',
				primaryBorderColor: '#226A73',
				lineColor: '#226A73',
				tertiaryColor: '#eef6f7',
				fontFamily: 'Helvetica',
				fontSize: '13px'
			},
			flowchart: {
				curve: 'linear',
				htmlLabels: true,
				nodeSpacing: 50,
				rankSpacing: 70
			}
		});

		const graphId = `graph_${Math.random().toString(36).slice(2)}`;
		const { svg } = await mermaid.render(graphId, definition);
		return normalizeSvgForPdf(svg);
	} catch (err) {
		console.warn('Mermaid render failed, falling back to empty SVG:', err);
		return '';
	}
}

function hasMermaidNodes(definition: string): boolean {
	const lines = definition
		.split('\n')
		.map((l) => l.trim())
		.filter((l) => l.length > 0 && !l.startsWith('%%'));
	return lines.some((l) => !/^flowchart\s+(LR|TB|RL|BT)$/i.test(l));
}

function normalizeSvgForPdf(svgMarkup: string): string {
	if (!svgMarkup) return '';
	const parser = new DOMParser();
	const doc = parser.parseFromString(svgMarkup, 'image/svg+xml');
	const svg = doc.documentElement;

	const measureHost = document.createElement('div');
	measureHost.style.position = 'fixed';
	measureHost.style.left = '-10000px';
	measureHost.style.top = '-10000px';
	measureHost.style.width = '1600px';
	measureHost.style.height = '1000px';
	measureHost.style.opacity = '0';
	measureHost.style.pointerEvents = 'none';
	measureHost.appendChild(svg.cloneNode(true));
	document.body.appendChild(measureHost);

	try {
		const mountedSvg = measureHost.querySelector('svg') as SVGSVGElement | null;
		if (!mountedSvg) return '';
		const graphRoot =
			mountedSvg.querySelector('g.output') ||
			mountedSvg.querySelector('g.flowchart') ||
			mountedSvg.querySelector('g');
		if (!graphRoot) return '';
		const bbox = (graphRoot as SVGGElement).getBBox();
		if (!bbox || (!bbox.width && !bbox.height)) return '';
		const padding = 24;
		const width = Math.max(900, Math.ceil(bbox.width + padding * 2));
		const height = Math.max(520, Math.ceil(bbox.height + padding * 2));

		mountedSvg.setAttribute(
			'viewBox',
			`${bbox.x - padding} ${bbox.y - padding} ${bbox.width + padding * 2} ${bbox.height + padding * 2}`
		);
		mountedSvg.setAttribute('width', String(width));
		mountedSvg.setAttribute('height', String(height));
		mountedSvg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
		mountedSvg.style.maxWidth = 'none';
		mountedSvg.style.background = 'white';

		return new XMLSerializer().serializeToString(mountedSvg);
	} catch (err) {
		console.warn('SVG normalization failed:', err);
		return '';
	} finally {
		document.body.removeChild(measureHost);
	}
}

function formatDate(): string {
	const date = new Date();
	return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

function titleCase(value: unknown): string {
	if (!value) return '-';
	return String(value)
		.replace(/[_-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/\b\w/g, (match) => match.toUpperCase());
}

function chunk<T>(list: T[], size: number): T[][] {
	const result: T[][] = [];
	for (let i = 0; i < list.length; i += size) result.push(list.slice(i, i + size));
	return result;
}

function escapeMermaidLabel(value: unknown): string {
	return String(value || '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/\n/g, '<br/>');
}

function buildMaps(data: LayoutData) {
	const channels = layoutChannels(data);
	return {
		loadsById: new Map(data.loads.map((item) => [item.id, item])),
		keypadsById: new Map(data.keypads.map((item) => [item.id, item])),
		keypadKeysById: new Map(data.keypadKeys.map((item) => [item.id, item])),
		channelsByLoadId: new Map(channels.map((item) => [item.load_id, item])),
		rcusById: new Map(data.rcus.map((item) => [item.id, item])),
		dinsById: new Map(data.dinModules.map((item) => [item.id, item]))
	};
}

function getChannelLabel(channel: MappedChannel | undefined, maps: ReturnType<typeof buildMaps>): string {
	if (!channel) return 'Unassigned';
	const objectId = channel.rcu_id ?? channel.din_module_id;
	const rcu = objectId ? maps.rcusById.get(objectId) : undefined;
	if (rcu || channel.source === 'rcu') return `RCU / Ch ${channel.channel_number}`;
	const din = channel.din_module_id ? maps.dinsById.get(channel.din_module_id) : undefined;
	if (din || channel.source === 'din') {
		const displayDin = din || { module_type: 'DIN', address: objectId };
		return `${titleCase(displayDin.module_type)} DIN ${displayDin.address ?? objectId} / Ch ${channel.channel_number}`;
	}
	return `Controller ${objectId} / Ch ${channel.channel_number}`;
}

function getButtonLabel(
	key: { keypad: number; key_number?: number; key_index?: number; name?: string | null },
	maps: ReturnType<typeof buildMaps>
): string {
	const keypad = maps.keypadsById.get(key.keypad);
	const address = keypad?.address ?? '?';
	const number = key.key_number ?? key.key_index ?? '?';
	return `${key.name?.trim() || `Button ${number}`} (KP ${address} / B${number})`;
}

function getActionLoads(
	action: { action_type?: string; action_target?: number },
	data: LayoutData,
	maps: ReturnType<typeof buildMaps>
) {
	if (action.action_type === 'load') {
		const load = action.action_target ? maps.loadsById.get(action.action_target) : undefined;
		return load ? [load] : [];
	}
	if (action.action_type === 'scene') {
		return data.sceneLoads
			.filter((item) => item.scene === action.action_target)
			.map((item) => maps.loadsById.get(sceneLoadTarget(item) ?? -1))
			.filter(Boolean);
	}
	return [];
}

function card(title: string, body: unknown, fillColor = COLORS.paper, accent = COLORS.teal): Content {
	return {
		table: { widths: ['*'], body: [[{ stack: [{ text: title, fontSize: 9, bold: true, color: COLORS.ink, margin: [0, 0, 0, 6] }, ...(Array.isArray(body) ? body : [body])], margin: [10, 9, 10, 9], fillColor }]] },
		layout: { hLineColor: () => accent, vLineColor: () => accent, hLineWidth: () => 0.8, vLineWidth: () => 0.8 }
	};
}

function buildSummary(data: LayoutData) {
	const electrical = data.electricalSummary || {};
	const e = electrical as Record<string, unknown>;
	const cap = (e.capacity_planning as Record<string, unknown>) || {};
	const rcuCap = (cap.rcu_capacity as Record<string, unknown>) || {};
	const dinCap = (cap.din_capacity as Record<string, unknown>) || {};
	const loadDist = (cap.load_distribution as Record<string, unknown>) || {};
	const power = (e.power_analysis as Record<string, unknown>) || {};
	const unitSummary = (e.unit as Record<string, unknown>) || {};
	return [
		card('Loads', { text: String(loadDist.total_loads || data.loads.length), fontSize: 18, bold: true }),
		card('RCUs', { text: String(unitSummary.total_rcus || data.rcus.length), fontSize: 18, bold: true }),
		card('DIN Modules', { text: String(dinCap.total_din_modules || data.dinModules.length), fontSize: 18, bold: true }),
		card('Total Wattage', { text: `${power.total_wattage || 0} W`, fontSize: 18, bold: true }, COLORS.successBg, COLORS.lime),
		card('RCU Utilization', { text: `${rcuCap.utilization_percentage || 0}%`, fontSize: 18, bold: true }),
		card('DIN Utilization', { text: `${dinCap.utilization_percentage || 0}%`, fontSize: 18, bold: true })
	];
}

function buildDinAllocation(data: LayoutData, maps: ReturnType<typeof buildMaps>) {
	const channels = layoutChannels(data);
	const byController: Array<{ title: string; subtitle: string; lines: string[] }> = [];
	for (const rcu of data.rcus) {
		const directLoads = channels
			.filter((channel) => channel.load_id && (channel.rcu_id === rcu.id || channel.source === 'rcu'))
			.sort((a, b) => (a.channel_number || 0) - (b.channel_number || 0))
			.map((channel) => {
				return `${getChannelLabel(channel, maps)}  -  ${maps.loadsById.get(channel.load_id)?.name || 'Unknown Load'}`;
			});
		byController.push({ title: rcu.name?.trim() || 'RCU', subtitle: 'Direct Outputs', lines: directLoads.length ? directLoads : ['No direct loads on RCU outputs'] });
	}
	for (const din of data.dinModules.slice().sort((a, b) => (a.address || 0) - (b.address || 0))) {
		const lines = channels
			.filter((channel) => channel.load_id && (channel.din_module_id === din.id || channel.source === 'din'))
			.sort((a, b) => (a.channel_number || 0) - (b.channel_number || 0))
			.map((channel) => {
				return `${getChannelLabel(channel, maps)}  -  ${maps.loadsById.get(channel.load_id)?.name || 'Unknown Load'}`;
			});
		byController.push({ title: `${titleCase(din.module_type)} DIN ${din.address ?? din.id}`, subtitle: 'Mounted on DIN Rail', lines: lines.length ? lines : ['No loads assigned'] });
	}
	return byController;
}

function buildDinCards(allocations: Array<{ title: string; subtitle: string; lines: string[] }>) {
	if (!allocations.length) return [card('Output Allocation', { text: 'No RCU or DIN allocation available.', fontSize: 9, color: COLORS.muted })];
	return chunk(allocations, 2).map((group) => ({ columns: group.map((item) => card(item.title, [{ text: item.subtitle, fontSize: 8, color: COLORS.muted, margin: [0, 0, 0, 6] }, ...item.lines.map((line) => ({ text: line, fontSize: 8.5, color: COLORS.ink, margin: [0, 1, 0, 1] }))], COLORS.paper, COLORS.teal)), columnGap: 10, margin: [0, 0, 0, 10] }));
}

function buildSystemTopologyDefinition(data: LayoutData, maps: ReturnType<typeof buildMaps>) {
	const channels = layoutChannels(data);
	const rcu = data.rcus[0];
	const keypads = data.keypads.slice().sort((a, b) => (a.address || 0) - (b.address || 0));
	const lines = ['flowchart LR'];
	if (rcu) lines.push(`  RCU["${escapeMermaidLabel((rcu.name?.trim() || 'RCU') + '<br/>Main Controller')}"]`);
	for (const din of data.dinModules.slice().sort((a, b) => (a.address || 0) - (b.address || 0))) {
		const loadLines = channels
			.filter((channel) => (channel.din_module_id === din.id || channel.source === 'din') && channel.load_id)
			.sort((a, b) => (a.channel_number || 0) - (b.channel_number || 0))
			.map((channel) => {
				return `Ch ${channel.channel_number}: ${maps.loadsById.get(channel.load_id)?.name || 'Load'}`;
			})
			.slice(0, 6);
		const nodeId = `DIN_${din.id}`;
		const label = `${titleCase(din.module_type)} DIN ${din.address ?? din.id}<br/>${loadLines.join('<br/>') || 'No loads assigned'}`;
		lines.push(`  ${nodeId}["${escapeMermaidLabel(label)}"]`);
		if (rcu) lines.push(`  RCU --> ${nodeId}`);
	}
	keypads.forEach((keypad, index) => {
		const nodeId = `KP_${keypad.id}`;
		const label = `${keypad.name?.trim() || `Keypad ${keypad.address}`}<br/>KP ${keypad.address}<br/>RS485 Node`;
		lines.push(`  ${nodeId}["${escapeMermaidLabel(label)}"]`);
		if (index === 0 && rcu) lines.push(`  RCU --> ${nodeId}`);
		if (index > 0) lines.push(`  KP_${keypads[index - 1].id} --> ${nodeId}`);
	});
	return lines.join('\n');
}

function buildOutputAllocationDefinition(data: LayoutData, maps: ReturnType<typeof buildMaps>) {
	const channels = layoutChannels(data);
	const lines = ['flowchart TB'];
	const rcu = data.rcus[0];
	if (rcu) lines.push(`  RCU["${escapeMermaidLabel((rcu.name?.trim() || 'RCU') + '<br/>Output Source')}"]`);
	for (const din of data.dinModules.slice().sort((a, b) => (a.address || 0) - (b.address || 0))) {
		const dinNode = `DIN_${din.id}`;
		lines.push(`  ${dinNode}["${escapeMermaidLabel(`${titleCase(din.module_type)} DIN ${din.address ?? din.id}`)}"]`);
		if (rcu) lines.push(`  RCU --> ${dinNode}`);
		const assignedChannels = channels
			.filter((channel) => (channel.din_module_id === din.id || channel.source === 'din') && channel.load_id)
			.sort((a, b) => (a.channel_number || 0) - (b.channel_number || 0));
		assignedChannels.forEach((channel) => {
			const load = maps.loadsById.get(channel.load_id);
			const loadNode = `LOAD_${din.id}_${channel.channel_number}`;
			lines.push(`  ${loadNode}["${escapeMermaidLabel(`Ch ${channel.channel_number}<br/>${load?.name || 'Load'}`)}"]`);
			lines.push(`  ${dinNode} --> ${loadNode}`);
		});
	}
	const directRcuChannels = channels
		.filter((channel) => channel.load_id && (channel.rcu_id != null || channel.source === 'rcu'))
		.sort((a, b) => (a.channel_number || 0) - (b.channel_number || 0));
	directRcuChannels.forEach((channel) => {
		const load = maps.loadsById.get(channel.load_id);
		const nodeId = `RLOAD_${channel.channel_number}`;
		lines.push(`  ${nodeId}["${escapeMermaidLabel(`RCU / Ch ${channel.channel_number}<br/>${load?.name || 'Load'}`)}"]`);
		if (rcu) lines.push(`  RCU --> ${nodeId}`);
	});
	return lines.join('\n');
}

function buildKeypadIntentRows(data: LayoutData, maps: ReturnType<typeof buildMaps>) {
	const rows: string[][] = [];
	for (const action of data.keyActions) {
		const key = maps.keypadKeysById.get(action.key);
		if (!key) continue;
		const loads = getActionLoads(action, data, maps);
		if (!loads.length) continue;
		rows.push([
			getButtonLabel(key, maps),
			titleCase(action.event_type),
			loads.map((load) => load?.name).join('\n'),
			loads.map((load) => getChannelLabel(maps.channelsByLoadId.get(load?.id || -1), maps)).join('\n')
		]);
	}
	return rows;
}

export async function generateElectricalLayoutPdf(data: LayoutData, filename = 'electrical_layout.pdf') {
	const pdfMake = await loadPdfMake();
	const maps = buildMaps(data);
	const summaryCards = buildSummary(data);
	const dinAllocations = buildDinAllocation(data, maps);
	const keypadIntentRows = buildKeypadIntentRows(data, maps);
	const topologySvg = await renderMermaidSvg(buildSystemTopologyDefinition(data, maps));
	const allocationSvg = await renderMermaidSvg(buildOutputAllocationDefinition(data, maps));

	const topologyBlock: Content[] = topologySvg
		? [{ svg: topologySvg, fit: [760, 430], margin: [0, 0, 0, 10] }]
		: [card('No Topology Data', { text: 'No RCU, DIN modules, or keypads are configured yet for this unit, so a system topology graph cannot be drawn.', fontSize: 9, color: COLORS.muted }, COLORS.infoBg, COLORS.teal)];

	const allocationBlock: Content[] = allocationSvg
		? [{ svg: allocationSvg, fit: [760, 430], margin: [0, 0, 0, 8] }]
		: [card('No Output Allocation', { text: 'No RCU or DIN module outputs are mapped to loads yet, so an output allocation graph cannot be drawn.', fontSize: 9, color: COLORS.muted }, COLORS.infoBg, COLORS.teal)];

	const content: Content[] = [
		{ columns: [{ image: LOGO_BASE64, width: 110 }, { stack: [{ text: 'TOYAMA CONTROLS & SYSTEMS', alignment: 'right', fontSize: 9, bold: true, color: COLORS.ink }, { text: 'Electrical Layout & Wiring Responsibility Sheet', alignment: 'right', fontSize: 20, bold: true, color: COLORS.ink, margin: [0, 4, 0, 0] }, { text: `${data.project.name} / ${data.unit.name}`, alignment: 'right', fontSize: 10, color: COLORS.muted, margin: [0, 4, 0, 0] }] }] },
		{ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 760, y2: 0, lineWidth: 1, lineColor: COLORS.teal }], margin: [0, 12, 0, 12] },
		{ columns: [{ text: `Generated ${formatDate()}`, fontSize: 8, color: COLORS.muted }, { text: `User ${localStorage.getItem('username') || 'N/A'}`, fontSize: 8, color: COLORS.muted, alignment: 'right' }], margin: [0, 0, 0, 12] },
		{ columns: summaryCards, columnGap: 8, margin: [0, 0, 0, 14] },
		{ columns: [card('Contractor Responsibility / Disclaimer', [{ text: 'This document records the intended GRMS low-voltage topology, output allocation, and keypad bus order for this unit.', fontSize: 9, color: COLORS.ink }, { text: 'Any deviation from this wiring document without written approval from Toyama may result in communication faults, unstable keypad behavior, dimming errors, or system failure.', fontSize: 8.5, color: COLORS.ink, margin: [0, 5, 0, 0] }, { text: 'This document should be retained as the approved reference during first-fix, second-fix, testing, and handover.', fontSize: 8.5, color: COLORS.ink, margin: [0, 5, 0, 0] }], COLORS.dangerBg, COLORS.teal), card('Mandatory Wiring Notes', [{ text: '- Prefer fresh new CAT6 cabling for the RS485 GRMS bus.', fontSize: 8.5, color: COLORS.ink }, { text: '- Do not reuse old, damaged, spliced, or uncertain data cabling.', fontSize: 8.5, color: COLORS.ink, margin: [0, 3, 0, 0] }, { text: '- Keep polarity consistent across the full RS485 chain.', fontSize: 8.5, color: COLORS.ink, margin: [0, 3, 0, 0] }, { text: '- Avoid star topology, loose joints, unlabelled terminations, and shared conduits with noisy mains where possible.', fontSize: 8.5, color: COLORS.ink, margin: [0, 3, 0, 0] }], COLORS.infoBg, COLORS.teal)], columnGap: 10, margin: [0, 0, 0, 14] },
		{ text: '1. System Topology Graph', fontSize: 12, bold: true, color: COLORS.ink, margin: [0, 0, 0, 8] },
		{ text: 'RCU to DIN relationship and RS485 keypad daisy chain.', fontSize: 8.5, color: COLORS.muted, margin: [0, 0, 0, 10] },
		...topologyBlock,
		{ table: { widths: ['*', '*'], body: [[{ stack: [{ text: 'Do', fontSize: 9, bold: true, color: COLORS.ink, margin: [0, 0, 0, 5] }, { text: '- Use daisy chain bus order as shown', fontSize: 8.5 }, { text: '- Use fresh CAT6 for RS485 bus', fontSize: 8.5, margin: [0, 3, 0, 0] }, { text: '- Label each keypad drop and termination', fontSize: 8.5, margin: [0, 3, 0, 0] }, { text: '- Test continuity and polarity before powering the system', fontSize: 8.5, margin: [0, 3, 0, 0] }], fillColor: COLORS.successBg, margin: [10, 8, 10, 8] }, { stack: [{ text: 'Do Not', fontSize: 9, bold: true, color: COLORS.ink, margin: [0, 0, 0, 5] }, { text: '- Do not star-wire keypads', fontSize: 8.5 }, { text: '- Do not mix old and new CAT6 on the same run', fontSize: 8.5, margin: [0, 3, 0, 0] }, { text: '- Do not leave floating joints or hidden splices', fontSize: 8.5, margin: [0, 3, 0, 0] }, { text: '- Do not energize outputs before verifying field terminations', fontSize: 8.5, margin: [0, 3, 0, 0] }], fillColor: COLORS.dangerBg, margin: [10, 8, 10, 8] }]] }, layout: { hLineColor: () => COLORS.line, vLineColor: () => COLORS.line }, margin: [0, 12, 0, 0], pageBreak: 'after' },
		{ text: '2. Output Allocation Graph', fontSize: 12, bold: true, color: COLORS.ink, margin: [0, 0, 0, 8] },
		{ text: 'DIN rails and direct RCU outputs mapped to their assigned loads.', fontSize: 8.5, color: COLORS.muted, margin: [0, 0, 0, 10] },
		...allocationBlock,
		{ pageBreak: 'after', text: '' },
		{ text: '3. Output Allocation Overview', fontSize: 12, bold: true, color: COLORS.ink, margin: [0, 0, 0, 8] },
		{ table: { widths: ['*'], body: [[{ text: 'This section shows what loads are landed on the RCU outputs and each DIN module. Use this during panel dressing, ferruling, tagging, and final continuity checks.', margin: [10, 8, 10, 8], fontSize: 8.5, color: COLORS.ink }]] }, layout: { hLineWidth: () => 0, vLineWidth: () => 0, fillColor: () => COLORS.infoBg }, margin: [0, 0, 0, 10] },
		...buildDinCards(dinAllocations),
		{ text: '4. Keypad Intent to Output Cross-Reference', fontSize: 12, bold: true, color: COLORS.ink, margin: [0, 6, 0, 8] },
		...buildKeypadIntentTable(keypadIntentRows),
		card('Final Contractor Sign-Off Reminder', [{ text: 'Before handing over the site, the electrical contractor should verify: bus continuity, keypad sequence, module addressing, CAT6 integrity, output tagging, ferrule labeling, and channel-to-load matching against this document.', fontSize: 8.5, color: COLORS.ink }, { text: 'If site wiring differs from this layout, the deviation must be recorded and approved in writing.', fontSize: 8.5, color: COLORS.ink, margin: [0, 5, 0, 0] }], COLORS.dangerBg, COLORS.teal)
	] as Content[];

	const docDefinition: TDocumentDefinitions = {
		pageSize: 'A4',
		pageOrientation: 'landscape',
		pageMargins: [26, 22, 26, 28],
		footer: (currentPage: number, pageCount: number) => ({ columns: [{ text: `Electrical Layout | ${data.unit.name}`, fontSize: 7, color: COLORS.muted, margin: [26, 8, 0, 0] }, { text: `Page ${currentPage} of ${pageCount}`, fontSize: 7, color: COLORS.muted, alignment: 'right', margin: [0, 8, 26, 0] }] }),
		defaultStyle: { fontSize: 8.5, color: COLORS.ink },
		content
	};

	pdfMake.createPdf(docDefinition).download(filename);
}

function buildKeypadIntentTable(rows: string[][]): Content[] {
	if (!rows.length) {
		return [
			card(
				'No Keypad Intent Data',
				{
					text: 'No keypad button actions are linked to output channels yet, so the cross-reference table is empty.',
					fontSize: 9,
					color: COLORS.muted
				},
				COLORS.infoBg,
				COLORS.teal
			)
		];
	}
	return [
		{
			table: {
				headerRows: 1,
				widths: [170, 55, 180, '*'],
				body: [
					['Source Button', 'State', 'Connected Loads', 'Output Channels'].map((text) => ({
						text,
						fontSize: 8,
						bold: true,
						color: COLORS.ink
					})),
					...rows
				]
			},
			layout: {
				hLineColor: () => COLORS.line,
				vLineColor: () => COLORS.line,
				fillColor: (rowIndex: number) => (rowIndex === 0 ? COLORS.paper : null)
			},
			margin: [0, 0, 0, 12]
		}
	];
}
